import * as React from 'react';
import { useSelector, useDispatch } from "react-redux";
import produce from 'immer';
//internal and external components import
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faComments, faFileInvoice, faPersonRunning, faHeart, faThumbtack, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { ReactNode } from 'react';
import CommentsDocsActionsIconWrapper from '../CommentsDocsActionsIconWrapper';
import { stableSort, getComparator, sortData } from '../../common/SortSupport';
import { EnhancedTableHead } from '../EnhancedTableHead';
import { colors } from '../../common/pallette/colors';
import { useNavigate } from 'react-router-dom';
import { setCurrentClub, setInitialClub } from '../../redux/reducers/clubDetails';
import TableSkeleton from '../TableSkeleton';
import { restCommon, setDrawer, setMyClubs } from '../../redux/reducers/common';
import { managePinnedClubs } from '../../api/common';
import { useTranslation } from 'react-i18next';
import { resetActionPlan } from '../../redux/reducers/actionPlan';
import { resetClubData } from '../../redux/reducers/clubData';
import { resetDataCaptures } from '../../redux/reducers/dataCaptures';
import { resetEvaluation } from '../../redux/reducers/evaluation';
import { resetGoalsAndStrategy } from '../../redux/reducers/goalsAndStrategy';
import { resetLibraryData } from '../../redux/reducers/library';

export default function MyClubsTable(props: any) {
  const { favourites, isLoading, manageIsPinnedClick } = props;
  const myClubs = useSelector((state: any) => state.common.myClubs);
  const displayMyClubs = useSelector((state: any) => state.common.displayMyClubs);
  const searchString = useSelector((state: any) => state.common.adminPageSearchString);
  const leagues = useSelector((state: any) => state.common.leagues);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [t] = useTranslation();

  //supporting contents
  const [order, setOrder] = React.useState('');
  const [orderBy, setOrderBy] = React.useState('');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const visibleRows: any = React.useMemo(
    () =>
      displayMyClubs && displayMyClubs?.length > 0 &&
      sortData(displayMyClubs, orderBy, order),
    [order, orderBy, displayMyClubs],
  );

  const head = [
    {
      key: 'name', displayName: 'club',
    },
    // {
    //   key: 'academyDirector', displayName: t('DC_1_ACADEMY_DIRECTOR'),
    // },
    {
      key: 'leagueId', displayName: t('LEAGUE'),
    },
    {
      key: 'currentRating', displayName: t('CURRENT_STAR'),
    },
    {
      key: 'targetRating', displayName: t('TARGET_STAR'),
    },
    // {
    //   key: '', displayName: '',
    // },
    {
      key: 'isFavourite', displayName: '',
    },
  ]

  const manageClubClick = (data: any) => {
    const d: any = { id: data.id, name: data.name, color: data.color, logo: data.imageUrl, currentEvaluationPhase: data.currentEvaluationPhase };
    dispatch(setCurrentClub(d));
    dispatch(resetActionPlan());
    dispatch(resetGoalsAndStrategy());
    dispatch(resetClubData());
    dispatch(resetDataCaptures());
    dispatch(resetEvaluation());
    dispatch(resetLibraryData());
    const moduleName:any = 'Evaluation';
    dispatch(setDrawer(moduleName));
    navigate('/evaluation/criteria');
  }

  const findLeague = (league: any) => {
    const result = leagues.find((x: any) => { return x.id == league });
    return result.name;
  }

  return (
    <TableContainer sx={{ height: '100%' }}>
      <Table
        stickyHeader
        sx={{
          boxShadow: 'none',
          '& .MuiTableCell-head': {
            backgroundColor: '#fff',
            zIndex: 3,
          },
        }}
        size="small"
        aria-label="a dense table"
      >
        <EnhancedTableHead
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          tableHeads={head}
          bg="#fff"
        />
        <TableBody>
          {
            !isLoading && visibleRows?.length > 0 ?
              visibleRows.map((row: any, index: any) => (
                <TableRow
                  key={`${row.name}_${index}`}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  className='hoverBG'
                >
                  <TableCell
                    width={300}
                    component="th"
                    scope="row"
                    className='cursorPointer'
                    onClick={() => { manageClubClick(row) }}
                    sx={{ overflow: 'hidden', py: '8px' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          flexShrink: 0,
                          borderRadius: '4px',
                          overflow: 'hidden',
                          backgroundColor: colors.grey.bright,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `1px solid ${colors.grey.light}`,
                        }}
                      >
                        {row?.imageUrl ? (
                          <Box
                            component="img"
                            src={`https://jamp.football${row.imageUrl}`}
                            alt=""
                            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: colors.grey.dark }}>
                            {row?.name?.[0]}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontFamily: 'Noto Sans',
                          fontWeight: 600,
                          color: colors.black.dark,
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                        }}
                      >
                        {row.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  {/* <TableCell component="th" scope="row">
                    {row.academyDirector}
                  </TableCell> */}
                  <TableCell component="th" scope="row">
                    {findLeague(row.leagueId)}
                  </TableCell>
                  <TableCell align="left">
                    {
                      Array.from({ length: 4 }, (_, i: any) => <FontAwesomeIcon icon={faStar} style={i < row.currentRating ? { color: colors.yellow.bright, } : { color: colors.grey.light, }} />)
                    }
                  </TableCell>
                  <TableCell align="left">
                    {
                      Array.from({ length: 4 }, (_, i: any) => <FontAwesomeIcon icon={faStar} style={i < row.targetRating ? { color: colors.yellow.bright, } : { color: colors.grey.light, }} />)
                    }
                  </TableCell>
                  <TableCell align='left'>
                    <FontAwesomeIcon className='icon cursorPointer' icon={faThumbtack} onClick={() => manageIsPinnedClick(row)} style={{ color: row.isPinned ? colors.grey.dark : colors.grey.light, }} />
                  </TableCell>
                </TableRow>
              )) :
              (
                isLoading ?
                  <TableSkeleton rowCount={8} cellCount={5} /> :
                  <TableCell colSpan={5} align='center' sx={{ border: 'none', py: 5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                      <Box sx={{
                        width: 44, height: 44, borderRadius: '50%',
                        backgroundColor: searchString ? 'rgba(224,68,68,0.08)' : `${colors.grey.dark}12`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <FontAwesomeIcon
                          icon={faMagnifyingGlass}
                          style={{ fontSize: '17px', color: searchString ? '#D94040' : colors.grey.dark, opacity: 0.6 }}
                        />
                      </Box>
                      <Box sx={{ fontSize: '12px', color: colors.grey.dark, opacity: 0.7, lineHeight: 1.5 }}>
                        {searchString
                          ? <>No clubs match <strong>"{searchString}"</strong></>
                          : 'No clubs to display'
                        }
                      </Box>
                    </Box>
                  </TableCell>
              )
          }
        </TableBody>
      </Table>
    </TableContainer>
  );
}